'use client';

import { Check, Minus } from 'lucide-react';
import AnimateOnScroll from '@/components/AnimateOnScroll';
import { Card } from '@/components/sections/kit';
import GridSection from '@/components/site/GridSection';
import { COMPARISON, CURRENCY, ROADMAP, TIERS, type Cell } from './data';

const premiumPrice = TIERS.find((t) => t.id === 'premium')?.price;

const PLANS = [
  { id: 'free', name: 'Free', sub: `${CURRENCY}0/mo` },
  { id: 'premium', name: 'Premium', sub: `from ${CURRENCY}${premiumPrice?.annual}/mo` },
  { id: 'enterprise', name: 'Enterprise', sub: 'custom' },
] as const;

/** Subtle spine on the Premium column. */
const PREMIUM_TINT = 'bg-brand-600/[0.03]';

function CellValue({ value }: { value: Cell }) {
  if (value === true) return <Check size={16} className="mx-auto text-data-green" aria-label="Included" />;
  if (value === false) return <Minus size={16} className="mx-auto text-line-3" aria-label="Not included" />;
  if (typeof value === 'object')
    // Dated roadmap pill — amber mono, deliberately never a checkmark.
    return (
      <span className="whitespace-nowrap rounded bg-data-amber/15 px-2 py-[3px] font-jetbrains text-[9.5px] font-semibold tracking-[0.07em] text-[color-mix(in_srgb,var(--color-data-amber)_70%,var(--color-ink))]">
        {value.tag}
      </span>
    );
  return <span className="tabular-nums">{value}</span>;
}

/** Full feature comparison across the three plans. Row labels link to the
 *  page that owns the feature. */
export default function ComparisonSection() {
  return (
    <GridSection id="compare">
      <AnimateOnScroll animation="fade-up">
        <div data-grid-block>
          <span className="block font-jetbrains text-[11px] font-semibold tracking-[0.16em] text-brand-600">
            COMPARE PLANS
          </span>
          <h2 className="mt-4 font-onest text-[clamp(28px,3.2vw,36px)] font-extrabold leading-[1.12] tracking-[-0.02em] text-ink">
            Every feature, side by side.
          </h2>
          <p className="mt-3.5 max-w-[560px] text-[15px] leading-[1.6] text-pretty text-muted">
            Roadmap rows carry dates, never checkmarks. Row labels link to the
            page that owns the feature.
          </p>
        </div>
      </AnimateOnScroll>

      <AnimateOnScroll animation="fade-up" delay={80}>
        <div data-grid-block className="mt-9">
          <Card shadow className="overflow-hidden">
            <div className="overflow-x-auto">
              <div className="grid min-w-[760px] grid-cols-[minmax(240px,1.5fr)_repeat(3,minmax(150px,1fr))]">
                {/* Header row */}
                <div className="border-b border-line bg-surface-2/60 px-6 py-4" />
                {PLANS.map((plan) => (
                  <div
                    key={plan.id}
                    className={`border-b border-line bg-surface-2/60 px-4 py-4 text-center ${
                      plan.id === 'premium' ? PREMIUM_TINT : ''
                    }`}
                  >
                    <div
                      className={`text-sm font-bold ${
                        plan.id === 'premium' ? 'text-brand-600' : 'text-ink'
                      }`}
                    >
                      {plan.name}
                    </div>
                    <div className="mt-0.5 text-[12px] text-dim">{plan.sub}</div>
                  </div>
                ))}

                {COMPARISON.map((group) => (
                  <div key={group.category} className="col-span-4 grid grid-cols-subgrid">
                    {/* Category divider row */}
                    <div className="col-span-4 border-b border-line bg-surface-2/60 px-6 py-2.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-dim">
                      {group.category}
                    </div>
                    {group.rows.map((row) => (
                      <div
                        key={row.label}
                        className="col-span-4 grid grid-cols-subgrid transition-colors duration-150 hover:bg-surface-2/50"
                      >
                        <div className="flex items-center border-b border-line px-6 py-3.5 text-sm text-muted">
                          {row.href ? (
                            <a
                              href={row.href}
                              className="hover:text-ink hover:underline hover:underline-offset-[3px]"
                            >
                              {row.label}
                            </a>
                          ) : (
                            row.label
                          )}
                        </div>
                        {(['free', 'premium', 'enterprise'] as const).map((plan) => (
                          <div
                            key={plan}
                            className={`flex items-center justify-center border-b border-line px-4 py-3.5 text-center text-sm text-ink ${
                              plan === 'premium' ? PREMIUM_TINT : ''
                            }`}
                          >
                            <CellValue value={row[plan]} />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </Card>
          <p className="mt-3.5 font-jetbrains text-[10px] tracking-[0.12em] text-dim">
            ONE SOURCE OF TRUTH &mdash; /COMPARE/* AND THE HOMEPAGE FAQ CITE THESE
            SAME NUMBERS &middot; ROADMAP: WEBHOOKS {ROADMAP.webhooks} &middot;
            NAMESERVERS {ROADMAP.nameservers}
          </p>
        </div>
      </AnimateOnScroll>
    </GridSection>
  );
}
